import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { paymentsAPI } from "../lib/api";

const MAX_POLLS = 8;
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | expired
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    let timer = null;
    const poll = async () => {
      attempts += 1;
      try {
        const data = await paymentsAPI.status(sessionId);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        if (attempts >= MAX_POLLS) {
          setState("timeout");
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (attempts >= MAX_POLLS) {
          setState("failed");
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();
    return () => { if (timer) clearTimeout(timer); };
  }, [sessionId]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-3xl shadow-sm p-8 text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-[#e86a24] animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-black text-[#1d3d3a]">Verifica pagamento…</h1>
            <p className="text-slate-600 mt-2 text-sm">Stiamo confermando con Stripe. Non chiudere la pagina.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-black text-[#1d3d3a] flex items-center justify-center gap-2">
              Pagamento riuscito! <Sparkles className="w-6 h-6 text-amber-400" />
            </h1>
            <p className="text-slate-600 mt-2 text-sm">
              {info?.package_id === "business_subscription_year" ? (
                <>La tua <strong>iscrizione Azienda</strong> è ora <strong>attiva per 1 anno</strong>. Ora puoi pubblicare annunci illimitati a €0,50 cad.</>
              ) : info?.package_id === "business_listing_fee" ? (
                <>Il tuo annuncio è ora <strong>pubblicato e visibile</strong> nel marketplace.</>
              ) : (
                <>Il tuo annuncio è ora <strong>in Vetrina Premium</strong> ed è visibile in evidenza nella home a tutti gli utenti.</>
              )}
            </p>
            <div className="mt-6 flex flex-col gap-2">
              {info?.listing_id && (
                <Button onClick={() => navigate(`/annuncio/${info.listing_id}`)} className="bg-[#e86a24] hover:bg-[#c8561a] rounded-full" data-testid="payment-success-view-listing">
                  Vedi il mio annuncio
                </Button>
              )}
              {info?.package_id === "business_subscription_year" && (
                <Button onClick={() => navigate("/pubblica")} className="bg-[#e86a24] hover:bg-[#c8561a] rounded-full" data-testid="payment-success-publish">
                  Pubblica il primo annuncio
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate("/")} className="rounded-full">
                Vai alla home
              </Button>
            </div>
          </>
        )}
        {(state === "expired" || state === "failed" || state === "timeout") && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-9 h-9" />
            </div>
            <h1 className="text-2xl font-black text-[#1d3d3a]">
              {state === "expired" ? "Sessione scaduta" : "Pagamento non confermato"}
            </h1>
            <p className="text-slate-600 mt-2 text-sm">
              {state === "timeout"
                ? "Stiamo ancora aspettando la conferma. Riceverai una notifica appena il pagamento sarà processato."
                : "Il pagamento non è andato a buon fine. Nessun importo è stato addebitato."}
            </p>
            <Button onClick={() => navigate("/profilo")} className="mt-5 bg-[#1d3d3a] hover:bg-[#0e2625] rounded-full">
              Torna al profilo
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
